import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type NanoBananaVersion = "2.0" | "2.1";
type NanoBananaVersionResponse = {
  nano_banana_version: NanoBananaVersion;
  changedBy: string | null;
  changedAt: string | null;
  effective: {
    savedVersion: NanoBananaVersion;
    generate: string;
    edit: string;
    generateOverride: boolean;
    editOverride: boolean;
  };
};

const settingsKey = ["/api/admin/nano-banana-version"];

function formatTime(value: string | null) {
  if (!value) return "No change recorded";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function NanoBananaVersionControl() {
  const queryClient = useQueryClient();
  const [requestedVersion, setRequestedVersion] = useState<NanoBananaVersion | null>(null);
  const query = useQuery<NanoBananaVersionResponse>({
    queryKey: settingsKey,
    queryFn: async () => (await apiRequest("GET", settingsKey[0])).json(),
    retry: false,
  });
  const save = useMutation({
    mutationFn: async (version: NanoBananaVersion) =>
      (await apiRequest("PUT", settingsKey[0], {
        nano_banana_version: version,
        confirmed: true,
      })).json() as Promise<NanoBananaVersionResponse>,
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKey, data);
      setRequestedVersion(null);
    },
  });

  const selected = query.data?.nano_banana_version ?? "2.1";

  return (
    <section className="space-y-3 border-t px-4 py-4" data-testid="nano-banana-version-control">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h4 className="text-sm font-medium">Nano Banana version (global)</h4>
          <p className="text-xs text-muted-foreground">
            Select the Fal Nano Banana version used app-wide. Rollback to 2.0 is supported.
          </p>
          {query.isPending ? (
            <div className="mt-3 space-y-2" aria-label="Loading Nano Banana version">
              <div className="h-3 w-32 animate-pulse rounded bg-muted" />
              <div className="h-3 w-56 animate-pulse rounded bg-muted/70" />
            </div>
          ) : query.isError ? (
            <div role="alert" className="mt-3 space-y-2 text-xs text-destructive">
              <p>Could not load the Nano Banana version: {(query.error as Error).message}</p>
              <Button size="sm" variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
                {query.isFetching ? "Retrying" : "Retry"}
              </Button>
            </div>
          ) : query.data ? (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                <span>Saved value: <strong className="font-mono">{query.data.nano_banana_version}</strong></span>
                <span className="text-muted-foreground">Last actor: {query.data.changedBy ?? "Not recorded"}</span>
                <span className="text-muted-foreground">Last changed: {formatTime(query.data.changedAt)}</span>
              </div>
              <div className="grid gap-2 text-xs sm:grid-cols-2">
                <div className="rounded-md border p-2.5">
                  <div className="font-medium">Effective generation endpoint</div>
                  <code className="mt-1 block break-all text-muted-foreground">{query.data.effective.generate}</code>
                  {query.data.effective.generateOverride && (
                    <p className="mt-1 text-amber-700 dark:text-amber-400">Override active. This call does not use the global version.</p>
                  )}
                </div>
                <div className="rounded-md border p-2.5">
                  <div className="font-medium">Effective edit endpoint</div>
                  <code className="mt-1 block break-all text-muted-foreground">{query.data.effective.edit}</code>
                  {query.data.effective.editOverride && (
                    <p className="mt-1 text-amber-700 dark:text-amber-400">Override active. This call does not use the global version.</p>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Effective saved version: <span className="font-mono">{query.data.effective.savedVersion}</span>
              </p>
            </div>
          ) : null}
        </div>
        {!query.isPending && !query.isError && query.data && (
          <div className="flex shrink-0 gap-2" aria-label="Choose Nano Banana version">
            {(["2.0", "2.1"] as const).map((version) => (
              <Button
                key={version}
                size="sm"
                variant={selected === version ? "secondary" : "outline"}
                disabled={selected === version || save.isPending}
                onClick={() => setRequestedVersion(version)}
                data-testid={`button-select-nano-banana-${version}`}
              >
                {version}
              </Button>
            ))}
          </div>
        )}
      </div>
      {save.isPending && <p role="status" className="text-xs text-muted-foreground">Saving global version</p>}
      {save.isError && (
        <p role="alert" className="text-xs text-destructive">
          Could not save the global version: {(save.error as Error).message}
        </p>
      )}
      <AlertDialog open={requestedVersion !== null} onOpenChange={(open) => {
        if (!open && !save.isPending) setRequestedVersion(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change the global Nano Banana version?</AlertDialogTitle>
            <AlertDialogDescription>
              This affects every Fal Nano Banana generation and edit call app-wide. In-flight requests keep their original endpoint. Cancel sends no request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={save.isPending} data-testid="button-cancel-nano-banana-version">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={save.isPending || requestedVersion === null}
              onClick={(event) => {
                event.preventDefault();
                if (requestedVersion) save.mutate(requestedVersion);
              }}
              data-testid="button-confirm-nano-banana-version"
            >
              {save.isPending ? "Saving" : `Confirm ${requestedVersion ?? ""}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
